import { Direction, ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import * as React from "react";

import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";

// Segmented control: a row of mutually exclusive options that reads as one
// switch. Used for settings that have a handful of fixed choices (theme,
// language) where a dropdown hides the options behind a tap.

function ToggleGroup({
  className,
  dir,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
  const { dir: langDir } = useLanguage();
  const resolvedDir = dir ?? langDir;

  return (
    <Direction.Provider dir={resolvedDir}>
      <ToggleGroupPrimitive.Root
        data-slot="toggle-group"
        dir={resolvedDir}
        className={cn(
          "tw:inline-flex tw:items-center tw:gap-0.5 tw:rounded-full tw:border tw:border-input tw:bg-muted/50 tw:p-0.5",
          className,
        )}
        {...props}
      />
    </Direction.Provider>
  );
}

function ToggleGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item>) {
  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      className={cn(
        "tw:inline-flex tw:h-8 tw:min-w-9 tw:items-center tw:justify-center tw:gap-1.5 tw:rounded-full tw:border tw:border-transparent tw:px-2.5 tw:text-xs tw:font-semibold tw:text-muted-foreground tw:transition-colors",
        "tw:hover:text-foreground",
        "tw:data-[state=on]:bg-background tw:data-[state=on]:text-foreground tw:data-[state=on]:shadow-sm",
        "tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:focus-visible:outline-none",
        "tw:disabled:pointer-events-none tw:disabled:opacity-50",
        "tw:[&_svg]:size-4 tw:[&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

export { ToggleGroup, ToggleGroupItem };
